<script setup lang="ts">
import { setup } from '@quasarcast/vue-service'
import { SplashScreen } from '@capacitor/splash-screen'
import { EdgeToEdge } from '@capawesome/capacitor-android-edge-to-edge-support'
import { providers } from './providers'
import { useQrScanner } from 'modules/QrCode/composables/useQrScanner'
import { useAppHider } from './shared/composables/useHideApp/useAppHider'
import { useAuthState } from 'services/auth/useAuthState'
import { useAuthenticatedRedirector, useUnauthenticatedRedirector } from '@vueauth/core'
import { Loading } from 'quasar'
import { mdiFlashlight, mdiFlashlightOff } from '@quasar/extras/mdi-v7'
import { useRouter } from 'vue-router'
import { unref } from 'vue'
import useDeviceStore from './modules/Bluetooth/services/devices/useDeviceStore'
import { Capacitor } from '@capacitor/core'
import { registerOfflineQueueBackground } from 'src/packages/orion-pinia-orm/queue/offlineQueueBackground'
import { createOfflineQueueBackgroundDeps } from 'src/packages/orion-pinia-orm/queue/offlineQueueBackgroundDeps'
import { markAuthenticatedInteractive } from 'src/auth/authenticatedInteractive'

// Capawesome applies WebView inset margins. Play still requires the native
// androidx.activity.EdgeToEdge.enable(this) call in MainActivity (patched at
// trapeze time) so Android 15+ and older APIs share the same window insets.
if (Capacitor.getPlatform() === 'android') {
  EdgeToEdge.enable().catch(console.error)
}

SplashScreen.hide()
const qrScanner = useQrScanner()
const appHider = useAppHider()

const router = useRouter()

const authenticatedRedirector = useAuthenticatedRedirector({ redirectTo: { name: 'select.tenant.org' }, router })
const unauthenticatedRedirector = useUnauthenticatedRedirector({ redirectTo: { name: 'auth.login' }, router })

function hideLoadingAndMarkInteractive () {
  Loading.hide()
  markAuthenticatedInteractive()
}

authenticatedRedirector.onChecked.value = hideLoadingAndMarkInteractive
unauthenticatedRedirector.onChecked.value = hideLoadingAndMarkInteractive

router.isReady().then(() => {
  const route = unref(router.currentRoute)

  if (route.meta.authOnly) {
    Loading.show()
    unauthenticatedRedirector.execOnAuthStateEnsured()
  }

  if (route.meta.unauthOnly) {
    authenticatedRedirector.execOnAuthStateEnsured()
  }
})

router.beforeEach((to, from) => {
  if (to.meta.authOnly) {
    Loading.show()
    unauthenticatedRedirector.execOnAuthStateEnsured()
  }

  if (to.meta.unauthOnly) {
    authenticatedRedirector.execOnAuthStateEnsured()
  }
})

setup(providers)

if (Capacitor.getPlatform() !== 'web') {
  registerOfflineQueueBackground(createOfflineQueueBackgroundDeps()).catch((error: unknown) => {
    console.warn('offline queue background registration failed', error)
  })
}

const authState = useAuthState()
</script>

<template>
  <router-view
    :tenant-id="authState.tenantId.value"
    :org-id="authState.orgId.value"
    :class="[appHider.hidden.value ? 'hidden' : '']"
  />
  <q-card
    v-if="appHider.hidden.value"
    class="absolute-bottom full-width text-grey-8 row"
  >
    <q-btn
      label="back"
      class="col"
      size="lg"
      color="white"
      text-color="grey-9"
      unelevated
      @click="qrScanner.stopScan()"
    />
  </q-card>

  <div
    v-if="appHider.hidden.value && qrScanner.implementationName.value === 'html5'"
    class="absolute-full"
    style="margin-bottom: 54px"
  >
    <div
      id="web-scanner-target"
      class="full-width"
    >
      Scanner here
    </div>
  </div>

  <div
    v-if="appHider.hidden.value"
    class="absolute text-grey-8 row"
    style="bottom: 70px; right: 14px;"
  >
    <q-btn
      :color="qrScanner.withTorch.value ? 'amber-5' : 'grey'"
      size="xl"
      round
      :icon="qrScanner.withTorch.value ? mdiFlashlight : mdiFlashlightOff"
      @click="qrScanner.withTorch.value = !qrScanner.withTorch.value"
    />
  </div>
</template>

